import {
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Typography,
  useModal,
} from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Portal } from '@aloft/toolkit-sui/chakra';

import { ReactElement } from 'react';

import {
  VALIDATION_STEP_LABELS,
  fieldDisplayNames,
  routeToStepMap,
} from '../../../hooks/validation/consts.ts';
import { useAppraisifyNavigation } from '../../../hooks/validation/useAppraisifyNavigation.ts';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { useModuleContext } from '../../../modules/shared/moduleContext/useModuleContext.ts';
import { menuListProps } from '../consts.ts';
import { SubNavItemType, navItemHasChildren } from './types.ts';
import { useIsSelectedNavItem } from './useIsSelectedNavItem.ts';

const Contents = ({ label, isSelected }: { label: string; isSelected: boolean }) => (
  <>
    <Typography
      variant="body1"
      color={isSelected ? 'textPrimary' : 'textTertiary'}
      __dangerDoNotUseSx={{ fontWeight: isSelected ? '600' : undefined }}
    >
      {label}
    </Typography>
    <Typography
      // css hackery; prevent nav from jumping when different things are bolded
      __dangerDoNotUseSx={{ visibility: 'hidden', fontWeight: '600', height: 0 }}
    >
      {label}
    </Typography>
  </>
);

export const HeaderSubNavItem = ({ item }: { item: SubNavItemType }) => {
  const { label, route, parentRoute } = item;
  const { isStepEnabled, getFirstErrorStep } = useAppraisifyNavigation();
  const { errors } = useAppraisifyValidation();
  const { Modal, openModal, isModalOpen, closeModal } = useModal();
  const {
    navigation: { NavLink },
  } = useModuleContext();

  const isSelected = useIsSelectedNavItem(route);

  // Check if this route corresponds to a validation step
  const step = routeToStepMap[route];
  const isStepDisabled = step ? !isStepEnabled(step) : false;

  const handleClick = (e: React.MouseEvent) => {
    if (isStepDisabled) {
      e.preventDefault();
      e.stopPropagation();
      openModal();
    }
  };

  let Component: ReactElement;

  if (navItemHasChildren(item)) {
    Component = (
      <Menu>
        <MenuButton onClick={handleClick}>
          <Contents label={label} isSelected={isSelected} />
        </MenuButton>
        <Portal>
          <MenuList {...menuListProps}>
            {item.items.map((subItem) => {
              const subStep = routeToStepMap[subItem.route];
              const isSubStepDisabled = subStep ? !isStepEnabled(subStep) : false;

              return (
                <NavLink
                  key={subItem.route}
                  to={`${parentRoute}/${route}#${subItem.route}`}
                  onClick={(e: React.MouseEvent) => {
                    if (isSubStepDisabled) {
                      e.preventDefault();
                      e.stopPropagation();
                      openModal();
                    }
                  }}
                >
                  <MenuItem>
                    <Typography variant="body1" __testId={subItem.testID}>
                      {subItem.label}
                    </Typography>
                  </MenuItem>
                </NavLink>
              );
            })}
          </MenuList>
        </Portal>
      </Menu>
    );
  } else {
    Component = (
      <NavLink to={`${parentRoute}/${route}`} onClick={handleClick}>
        <Contents label={label} isSelected={isSelected} />
      </NavLink>
    );
  }

  const firstErrorStep = getFirstErrorStep();

  // Get the required fields for the first error step
  const getRequiredFields = () => {
    if (!firstErrorStep) return [];

    const stepErrors = errors[firstErrorStep];
    if (!stepErrors) return [];

    return Object.keys(stepErrors).map((fieldKey) => {
      const error = stepErrors[fieldKey as keyof typeof stepErrors];
      return {
        key: fieldKey,
        displayName: fieldDisplayNames[fieldKey] ?? fieldKey,
        message: error?.message ?? 'Required',
      };
    });
  };

  const requiredFields = getRequiredFields();

  return (
    <>
      <Flex
        paddingLeft="0.5rem"
        paddingRight="0.5rem"
        height="100%"
        alignItems="center"
        id={`${route}-nav`}
      >
        {Component}
      </Flex>

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title="Incomplete Required Fields"
          secondaryAction={{
            text: 'OK',
            onClick: closeModal,
          }}
        >
          <div style={{ marginBottom: '1rem' }}>
            <Typography variant="body1">
              Please complete all required fields in the&nbsp;
              <strong>
                {firstErrorStep
                  ? (VALIDATION_STEP_LABELS[firstErrorStep] ?? 'previous step')
                  : 'previous step'}
              </strong>
              &nbsp; before continuing to <strong>{label}</strong>.
            </Typography>
          </div>

          {requiredFields.length > 0 && (
            <div>
              <div style={{ marginBottom: '0.5rem' }}>
                <Typography variant="body2" __dangerDoNotUseSx={{ fontWeight: '600' }}>
                  Required fields:
                </Typography>
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                {requiredFields.map((field) => (
                  <li key={field.key}>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <Typography variant="body2">
                        <strong>{field.displayName}</strong>
                      </Typography>
                      {field.message !== 'Required' && (
                        <Typography variant="body2" color="textTertiary">
                          ({field.message})
                        </Typography>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};
